import { ERROR_CODE } from '@/shared/errors/codes'
import { AppError } from '@/shared/errors/envelope'

// `import type` (not inline `type`) so no runtime import of the schemas is
// emitted: builds without the mock do not need to ship them.
import type {
  LessonSummary,
  SessionDelivery,
  SessionSummary,
  SubmitItemInput,
  SubmitItemResponse,
} from './contract'

/**
 * The learning-session boundary shared by the UI and every implementation.
 *
 * Shaped after Backend Contracts v0.1 §4.4–§4.5 (`get_today_plan`,
 * `start_task_attempt`, `submit_attempt`) so a Supabase-backed implementation
 * can replace the mock without changing callers. Implementations validate
 * everything they return against the contract schemas.
 *
 * The client never grades: `submitItem` is the only source of correctness.
 */
export interface LearningService {
  /** Today's lesson for the learner, or null when none is available. */
  getTodayLesson(learnerId: string): Promise<LessonSummary | null>

  /**
   * Start or resume a session. Idempotent on `clientSessionId`: repeating the
   * call returns the same session.
   */
  startSession(
    learnerId: string,
    lessonId: string,
    clientSessionId: string,
  ): Promise<SessionDelivery>

  /**
   * Submit one item. Same `client_attempt_id` and same payload replays the
   * stored result; a different payload is `IDEMPOTENCY_CONFLICT`; a second
   * attempt at an already-submitted item is `ALREADY_SUBMITTED` (FR-41).
   */
  submitItem(input: SubmitItemInput): Promise<SubmitItemResponse>

  /** Finish the session and return its truthful summary. Idempotent. */
  completeSession(sessionId: string): Promise<SessionSummary>
}

/**
 * Used wherever no real learning backend exists yet — including every pilot
 * build. There is never a lesson, so Child Home shows an honest empty state
 * instead of fabricated activity (decision D-02).
 */
export function createUnavailableLearningService(): LearningService {
  const unavailable = () =>
    Promise.reject(new AppError(ERROR_CODE.RESOURCE_NOT_AVAILABLE))
  return {
    getTodayLesson: () => Promise.resolve(null),
    startSession: unavailable,
    submitItem: unavailable,
    completeSession: unavailable,
  }
}

/**
 * Used when a learning backend should exist but cannot be reached safely, for
 * example a local build pointed at a remote Supabase project. Every call
 * fails, so Child Home shows its "could not load" state instead of an empty
 * day or simulated results. Never falls back to the MOCK service.
 */
export function createNotConfiguredLearningService(): LearningService {
  const unavailable = () =>
    Promise.reject(new AppError(ERROR_CODE.RESOURCE_NOT_AVAILABLE))
  return {
    getTodayLesson: unavailable,
    startSession: unavailable,
    submitItem: unavailable,
    completeSession: unavailable,
  }
}

/**
 * Defers loading an implementation until first use, so it can live in a
 * separate chunk. A failed load is not cached: the next call retries.
 */
export function createLazyLearningService(
  load: () => Promise<LearningService>,
): LearningService {
  let pending: Promise<LearningService> | null = null
  const get = () => {
    pending ??= load().catch((error: unknown) => {
      pending = null
      throw error
    })
    return pending
  }
  return {
    getTodayLesson: async (learnerId) => (await get()).getTodayLesson(learnerId),
    startSession: async (learnerId, lessonId, clientSessionId) =>
      (await get()).startSession(learnerId, lessonId, clientSessionId),
    submitItem: async (input) => (await get()).submitItem(input),
    completeSession: async (sessionId) => (await get()).completeSession(sessionId),
  }
}
